import { useCallback, useContext, useEffect, useState } from "react";
import { Popconfirm } from "../../../BP2Components";
import { FormBuilderContext } from "../FormBuilderContext";
import { getFormActions } from "../../../../services/restAPI/FormsV3";
import IntlMessages from "../../../../util/IntlMessages";
import { MethodModal } from "./MethodModal";
import "./FormMethods.less";

interface SelectOption {
   value: string;
   label: string;
}

const FormMethods = () => {
   const {
      selectedForm,
      businessMethods: methods,
      setBusinessMethods: setMethods,
      previewMode,
   } = useContext(FormBuilderContext);
   const [availableActions, setAvailableActions] = useState<SelectOption[]>([]);
   // null = closed, -1 = adding new, >= 0 = editing at index
   const [editingIndex, setEditingIndex] = useState<number | null>(null);

   const formKey =
      typeof selectedForm === "object" && selectedForm !== null ? selectedForm.value : selectedForm;

   useEffect(() => {
      setAvailableActions([]);
      if (typeof formKey !== "string") return;
      const [module, model] = formKey.split("-");
      if (!module || !model || module === "primitives") return;

      let cancelled = false;
      getFormActions(module, model)
         .then((res: { data?: { name: string; label?: string }[] }) => {
            if (!cancelled && res?.data) {
               setAvailableActions(
                  res.data.map((action) => ({
                     value: action.name,
                     label: action.label || action.name,
                  })),
               );
            }
         })
         .catch(() => {
            if (!cancelled) setAvailableActions([]);
         });
      return () => {
         cancelled = true;
      };
   }, [formKey]);

   const handleModalOk = useCallback(
      (updatedMethod: (typeof methods)[number]) => {
         if (editingIndex !== null && editingIndex >= 0) {
            setMethods((prev) =>
               prev.map((method, i) => (i === editingIndex ? updatedMethod : method)),
            );
         } else {
            setMethods((prev) => [...prev, updatedMethod]);
         }
         setEditingIndex(null);
      },
      [editingIndex, setMethods],
   );

   const handleDelete = useCallback(
      (index: number) => setMethods((prev) => prev.filter((_, i) => i !== index)),
      [setMethods],
   );

   if (!selectedForm || previewMode) return null;

   const isModalOpen = editingIndex !== null;
   const editingMethod = editingIndex !== null && editingIndex >= 0 ? methods[editingIndex] : null;

   return (
      <div className="form-methods">
         <div className="form-methods-header">
            <strong>
               <IntlMessages id="form.methods.title" defaultMessage="Metody biznesowe" />
            </strong>
            <button
               type="button"
               className="form-methods-add-btn"
               onClick={() => setEditingIndex(-1)}
            >
               + <IntlMessages id="form.methods.add" defaultMessage="Dodaj metodę" />
            </button>
         </div>

         {methods.length > 0 && (
            <ul className="form-methods-list">
               {methods.map((method, index) => (
                  <li key={index} className="form-methods-item">
                     <span className="form-methods-item-label">
                        {method.label || method.name || (
                           <em>
                              <IntlMessages
                                 id="form.methods.unnamed"
                                 defaultMessage="(bez nazwy)"
                              />
                           </em>
                        )}
                     </span>
                     <span className="form-methods-item-name">{method.name}</span>
                     <div className="form-methods-item-actions">
                        <button
                           type="button"
                           aria-label="Edytuj metodę"
                           onClick={() => setEditingIndex(index)}
                        >
                           ✎
                        </button>
                        <Popconfirm
                           title={
                              <IntlMessages
                                 id="form.methods.deleteConfirm"
                                 defaultMessage="Usunąć metodę?"
                              />
                           }
                           onConfirm={() => handleDelete(index)}
                        >
                           <button type="button" aria-label="Usuń metodę">
                              ×
                           </button>
                        </Popconfirm>
                     </div>
                  </li>
               ))}
            </ul>
         )}

         <MethodModal
            isOpen={isModalOpen}
            onOk={handleModalOk}
            onCancel={() => setEditingIndex(null)}
            initialMethod={editingMethod}
            availableActions={availableActions}
         />
      </div>
   );
};

export default FormMethods;
